import { INCOME_ENTRY_STATUS, Roles } from "constants/index";
import {
  IIncomeEntryArticleBundle,
  IIncomeEntryArticle,
  IncomeEntryStatus,
  IncomeEntryBundleObservationReason,
} from "types"

export const REVIEW_STATUS_MAP: Record<string, {label: string; color: "warning" | "success"; finalButton: string;}> = {
  ok: {
    label: "Verificado en planta",
    color: "success",
    finalButton: "Confirmar e ingresar a stock",
  },
  with_observations: {
    label: "Verificado con observaciones",
    color: "warning",
    finalButton: "Ingresar a stock con observaciones",
  }
}

export const TABS: { status: IncomeEntryStatus; label: string; roles?: string[] }[] = [
  { status: INCOME_ENTRY_STATUS.PENDING, label: 'Pendientes' },
  { status: INCOME_ENTRY_STATUS.IN_PROGRESS, label: 'En progreso' },
  { status: INCOME_ENTRY_STATUS.VERIFIED, label: 'Verificados' },
  { status: INCOME_ENTRY_STATUS.FINISHED, label: 'Finalizados', roles: [Roles.ADMIN, Roles.SUPERADMIN, Roles.SUPERVISOR] },
]

export const STATUS_COLOR: Record<IncomeEntryStatus, 'error' | 'warning' | 'success' | 'secondary'> = {
  [INCOME_ENTRY_STATUS.PENDING]: 'error',
  [INCOME_ENTRY_STATUS.IN_PROGRESS]: 'warning',
  [INCOME_ENTRY_STATUS.VERIFIED]: 'success',
  [INCOME_ENTRY_STATUS.FINISHED]: 'secondary',
}

export const OBSERVATION_REASON_MAP: Record<IncomeEntryBundleObservationReason, string> = {
  damaged: 'Artículo dañado',
  dirty: 'Artículo sucio',
  wet: 'Artículo mojado',
  expired: 'Vencido',
  illegible_label: 'Etiqueta ilegible',
  missing_label: 'Sin etiqueta',
  missing: 'No llegó',
  missing_straps: 'Sin zunchos',
  other: 'Otro',
}

export const OBSERVATION_REASON_OPTIONS: IncomeEntryBundleObservationReason[] = [
  'damaged',
  'dirty',
  'wet',
  'missing_label',
  'missing',
  'missing_straps',
  'other',
]

export const formatIncomeEntryMeters = (
  amount: number,
  articleUnitsPerItem?: string | null,
): string => {
  const metersPerItem = Number(articleUnitsPerItem);
  if (!articleUnitsPerItem || !Number.isFinite(amount) || !Number.isFinite(metersPerItem) || metersPerItem <= 0)
    return '—';
  return `${(amount * metersPerItem).toLocaleString('es-AR', { maximumFractionDigits: 3 })} m`;
};

// helpers
// Footer progress
/**
 * A bundle is "resolved" if it was checked (arrived OK) or has an
 * observation recorded (arrived with problem, or didn't arrive).
 */
export const bundleResolved = (bun: IIncomeEntryArticleBundle): boolean => !!bun.arrived;

/** A product is complete when all its bundles are resolved. */
export const articleComplete = (art: IIncomeEntryArticle): boolean =>
  art.bundles.every(bundleResolved)

/** Number of resolved bundles within a product. */
export const countBundleResolved = (art: IIncomeEntryArticle): number =>
  art.bundles.filter(bundleResolved).length

/** Number of bundles with observation within a product. */
export const countBundleObserved = (art: IIncomeEntryArticle): number =>
  art.bundles.filter((b) => b.observations.length > 0).length

export interface VerificationProgress {
  totalArticles: number
  completedArticles: number
  totalBundles: number
  resolvedBundles: number
  pendingBundles: number
  totalObservations: number
  allResolved: boolean
  percentage: number
  fisicalAmount: number
  availableAmount: number
  unavailableAmount: number
}

/** Calculates overall verification progress from the products. */
export const calculateProgress = (articles: IIncomeEntryArticle[]): VerificationProgress => {
  const totalArticles = articles.length
  const completedArticles = articles.filter(a => articleComplete(a)).length
  const totalBundles = articles.reduce((n, p) => n + p.bundles.length, 0)
  const resolvedBundles = articles.reduce((n, p) => n + countBundleResolved(p), 0)
  const totalObservations = articles.reduce((n, p) => n + countBundleObserved(p), 0)
  const fisicalAmount = articles.reduce((n, p) => n + p.bundles.reduce((acc, bundle) => acc + (bundle.amount ?? 0), 0), 0)
  const availableAmount = articles.reduce(
    (n, p) => n + p.bundles.reduce((acc, bundle) => acc + (bundle.amount_available ?? 0), 0),
    0
  )
  const unavailableAmount = fisicalAmount - availableAmount

  return {
    totalArticles,
    completedArticles,
    totalBundles,
    resolvedBundles,
    pendingBundles: totalBundles - resolvedBundles,
    totalObservations,
    allResolved: completedArticles === totalArticles,
    percentage: totalBundles === 0 ? 0 : Math.round((resolvedBundles / totalBundles) * 100),
    fisicalAmount,
    availableAmount,
    unavailableAmount,
  }
}
